/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  SimpleGrid,
  Text,
  Checkbox,
  Button,
  InputGroup,
  Input,
  InputRightElement,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store/store';
import { getOrdersAction } from '../../../modules/orders/actions';
import DatePicker, { DatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styled from '@emotion/styled';
import OrderStatusBadge from '../../components/orderStatusBadge';
import { OrderDTO } from '../../../modules/orders/types';
import { ORDER_STATUS, OrderStatus } from '../../../constants/statusConfig';
import { selectFilteredOrdersByTab } from '../../../modules/orders/selectors';

const StyledDatePicker = styled(DatePicker as unknown as React.ComponentType<DatePickerProps>)`
  width: 150px;
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  &:hover {
    border-color: #cbd5e0;
  }
  &:focus {
    border-color: #3182ce;
    box-shadow: 0 0 0 1px #3182ce;
  }
`;

const PAGE_SIZE = 20;

const OrdersPage: React.FC = () => {
  const dispatch = useDispatch();
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tabIndex, setTabIndex] = useState<OrderStatus>(ORDER_STATUS.ALL);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    dispatch(getOrdersAction.request());
  }, [dispatch]);

  const filteredOrders = useSelector((state: RootState) =>
    selectFilteredOrdersByTab(tabIndex, startDate || null, endDate || null, searchTerm)(state),
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value);

  // const toggleOrderSelection = (orderId: string) => {
  //   setSelectedOrderIds(prev =>
  //     prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId],
  //   );
  // };

  const isAllSelected =
    (filteredOrders.length && selectedOrderIds.length === filteredOrders.length) || false;

  const toggleSelectAll = () => {
    setSelectedOrderIds(isAllSelected ? [] : filteredOrders.map(order => order.pk));
  };

  return (
    <Box p={6}>
      <Heading size="lg" mb={4}>
        Заказы
      </Heading>

      <Tabs onChange={index => setTabIndex(index as OrderStatus)} variant="unstyled" mb={4}>
        <TabList>
          <Tab _selected={{ fontWeight: 'bold', borderBottom: '2px solid black' }}>Все</Tab>
          <Tab _selected={{ fontWeight: 'bold', borderBottom: '2px solid black' }}>В работе</Tab>
          <Tab _selected={{ fontWeight: 'bold', borderBottom: '2px solid black' }}>Возвраты</Tab>
          <Tab _selected={{ fontWeight: 'bold', borderBottom: '2px solid black' }}>Архив</Tab>
        </TabList>

        <TabPanels>
          {['Все', 'В работе', 'Возвраты', 'Архив'].map(tabName => (
            <TabPanel key={tabName}>
              <Flex justify="space-between" mb={4}>
                <Box flex="1">
                  <Flex mb={4} justify="flex-end">
                    <InputGroup maxW="300px" mr={5}>
                      <Input
                        placeholder="Поиск по заказам"
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                      <InputRightElement pointerEvents="none">
                        <SearchIcon color="gray.500" />
                      </InputRightElement>
                    </InputGroup>

                    <StyledDatePicker
                      selected={startDate}
                      onChange={(date: Date | null) => setStartDate(date || undefined)}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      placeholderText="Начало"
                    />

                    <StyledDatePicker
                      selected={endDate}
                      onChange={(date: Date | null) => setEndDate(date || undefined)}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      placeholderText="Конец"
                    />
                  </Flex>

                  <SimpleGrid
                    columns={10}
                    gridTemplateColumns="1fr 1fr 2fr 1fr 1fr 1fr 1fr 1fr"
                    spacing={4}
                    py={2}
                    color="gray.500"
                    fontSize="sm"
                    w="100%"
                    bgColor="blackAlpha.50"
                  >
                    {/* <Text>Номер заказа</Text> */}
                    <Text>Добавлено</Text>
                    <Text>Наименование</Text>
                    <Text>Артикул</Text>
                    {/* <Text>Поставщик</Text>
                    <Text>Бренд</Text> */}
                    <Text>Комментарий</Text>
                    <Text>Статус</Text>
                    <Text>Ожид. срок</Text>
                    <Text>Количество</Text>
                    <Text>Цена</Text>
                  </SimpleGrid>

                  {filteredOrders.slice(0, PAGE_SIZE).map((order: OrderDTO) => (
                    <React.Fragment key={order.pk}>
                      {order.order_items.map((item: any, index: number) => (
                        <SimpleGrid
                          key={`${order.pk}-${index}`}
                          columns={10}
                          gridTemplateColumns="1fr 1fr 2fr 1fr 1fr 1fr 1fr 1fr"
                          spacing={4}
                          py={2}
                          borderBottom="1px solid #e2e8f0"
                        >
                          {/* <Text fontWeight="bold">{order.number || '—'}</Text> */}
                          <Text>
                            {new Date(order.created_at).toLocaleDateString()}{' '}
                            {new Date(order.created_at).toLocaleTimeString()}
                          </Text>
                          <Text>{item.good_name || '—'}</Text>
                          <Text>{item.article_number || '—'}</Text>
                          {/* <Text>{item.supplier || '—'}</Text>
                          <Text>{item.brand || '—'}</Text> */}
                          <Text>{item.comment || '—'}</Text>
                          <OrderStatusBadge status={order.status as OrderStatus} />
                          <Text>{item.delivery_term || '—'}</Text>
                          <Text>{item.amount || '—'}</Text>
                          <Text>{item.price || '—'} ₽</Text>
                        </SimpleGrid>
                      ))}
                    </React.Fragment>
                  ))}

                  {filteredOrders.length > PAGE_SIZE && (
                    <Button variant="outline" mt={4} width="full">
                      Показать следующие {PAGE_SIZE}
                    </Button>
                  )}
                </Box>
              </Flex>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default OrdersPage;
